import React, { useState, useRef, useEffect } from 'react';
import ProtocolMessageLayout from './ProtocolMessageLayout';
import { ProtocolMessage, isValidProtocolMessage } from '../../../types/protocols';
import { formatMessageTime } from '../../../utils/timeUtils';
import { useI18n } from '../../../contexts/I18nContext';
import { createAIFeedback } from '../../../utils/adminApiUtils';
import { useSnackbar } from 'notistack';
import copyIcon from '../../../../assets/copy.svg';
import shareIcon from '../../../../assets/share.svg';
import likeIcon from '../../../../assets/feedback-like.svg';
import likeIconOutline from '../../../../assets/feedback-like-outline.svg';
import dislikeIcon from '../../../../assets/feedback-dislike.svg';
import dislikeIconOutline from '../../../../assets/feedback-dislike-outline.svg';

export type MessageProps = {
    sender: 'user' | 'ai';
    time: string;
    type: 'text' | 'photo' | 'audio' | 'json';
    text?: string;
    title?: string;
    transcription?: string;
    specialty?: string;
    speakerId?: string;
    json?: ProtocolMessage;
    serverId?: number; // ID сообщения на сервере для предотвращения дубликатов
    onOpenInstruments?: (
        protocol: ProtocolMessage,
        serverId?: number,
        anchor?: { top: number; left: number }
    ) => void;
    onCopyProtocol?: (protocol: ProtocolMessage) => void;
    /** Скрыть кнопки фидбека (например, пока идёт генерация протокола) */
    isGeneratingProtocol?: boolean;
};

// Импортируем компонент для layout json-сообщения


const Message: React.FC<MessageProps> = ({ sender, time, type, text, title, transcription, speakerId, json, serverId, onOpenInstruments, onCopyProtocol, isGeneratingProtocol = false }) => {
    const { t } = useI18n();
    const alignmentClass = sender === 'user' ? 'message-right' : 'message-left';
    const [showTranscription, setShowTranscription] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isAudioLoaded, setIsAudioLoaded] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [feedbackComment, setFeedbackComment] = useState('');
    const [feedbackGiven, setFeedbackGiven] = useState<'like' | 'dislike' | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const handleLoadedData = () => {
                const audioDuration = audio.duration;
                if (isFinite(audioDuration) && audioDuration > 0) {
                    setDuration(audioDuration);
                    setIsAudioLoaded(true);
                } else {
                    setDuration(0);
                    setIsAudioLoaded(false);
                }
            };

            const handleLoadedMetadata = () => {
                const audioDuration = audio.duration;
                if (isFinite(audioDuration) && audioDuration > 0) {
                    setDuration(audioDuration);
                    setIsAudioLoaded(true);
                }
            };

            const handleError = (e: Event) => {
                setDuration(0);
                setIsAudioLoaded(false);
            };

            const handleCanPlay = () => {
                const audioDuration = audio.duration;
                if (isFinite(audioDuration) && audioDuration > 0) {
                    setDuration(audioDuration);
                    setIsAudioLoaded(true);
                }
            };

            audio.addEventListener('loadeddata', handleLoadedData);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('canplay', handleCanPlay);
            audio.addEventListener('error', handleError);

            audio.load();

            return () => {
                audio.removeEventListener('loadeddata', handleLoadedData);
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                audio.removeEventListener('canplay', handleCanPlay);
                audio.removeEventListener('error', handleError);
            };
        }
    }, [text]);

    const handleToggleTranscription = () => {
        setShowTranscription(!showTranscription);
    };

    const handleAudioPlay = () => {
        setIsPlaying(true);
    };

    const handleAudioPause = () => {
        setIsPlaying(false);
    };

    const handleAudioTimeUpdate = () => {
        if (audioRef.current) {
            const currentAudioTime = audioRef.current.currentTime;
            if (isFinite(currentAudioTime)) {
                setCurrentTime(currentAudioTime);
            }
        }
    };

    const handleAudioClick = () => {
        if (audioRef.current && isAudioLoaded) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch(() => { });
            }
        }
    };

    const formatAudioTime = (time: number) => {
        if (!isFinite(time) || time < 0) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progressPercentage = (duration > 0 && isFinite(duration)) ? Math.min((currentTime / duration) * 100, 100) : 0;

    const handleCopyProtocol = () => {
        if (json) onCopyProtocol?.(json);
    };

    const handleOpenInstruments = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (!json) return;

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const anchor = {
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
        };

        onOpenInstruments?.(json, serverId, anchor);
    };

    const aiResponseText = type === 'json'
        ? (text || (json ? JSON.stringify(json) : ''))
        : (text || '');

    const aiResponseTrimmed = aiResponseText.trim();
    const isAiTypingPlaceholder =
        aiResponseTrimmed === t('dialogs.aiTyping') ||
        aiResponseTrimmed.toLowerCase().includes('typing') ||
        aiResponseTrimmed.toLowerCase().includes('печата');

    const isAiResponseReady = sender === 'ai' && aiResponseTrimmed.length > 0 && !isAiTypingPlaceholder;
    const showFeedbackUi = isAiResponseReady && !isGeneratingProtocol;

    useEffect(() => {
        if (isGeneratingProtocol) {
            setIsFeedbackModalOpen(false);
        }
    }, [isGeneratingProtocol]);

    const handleLikeClick = () => {
        if (!showFeedbackUi || feedbackGiven) return;
        setFeedbackGiven('like');
        enqueueSnackbar(t('dialogs.feedback.thanksLike'), {
            variant: 'success',
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
    };

    const openFeedbackModal = () => {
        if (!showFeedbackUi || feedbackGiven) return;
        setFeedbackComment('');
        setIsFeedbackModalOpen(true);
    };

    const closeFeedbackModal = () => {
        if (isSubmittingFeedback) return;
        setIsFeedbackModalOpen(false);
    };

    const handleSubmitFeedback = async () => {
        if (!aiResponseText.trim()) {
            enqueueSnackbar(t('dialogs.feedback.emptyAiResponse'), {
                variant: 'warning',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
            return;
        }

        try {
            setIsSubmittingFeedback(true);
            await createAIFeedback({
                message_id: serverId,
                ai_response_text: aiResponseText,
                user_comment: feedbackComment.trim() || undefined,
            });
            setFeedbackGiven('dislike');
            enqueueSnackbar(t('dialogs.feedback.sent'), {
                variant: 'success',
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
            setIsFeedbackModalOpen(false);
            setFeedbackComment('');
        } catch (error) {
            enqueueSnackbar(
                error instanceof Error ? error.message : t('dialogs.feedback.sendError'),
                {
                    variant: 'error',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                }
            );
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    // Новый рендер для json-сообщения
    if (type === 'json' && json && isValidProtocolMessage(json)) {
        return (
            <div>
                <div
                    className={`message ${alignmentClass} protocol-message`}
                    onClick={handleOpenInstruments}
                    onKeyDown={(e) => e.key === 'Enter' && handleOpenInstruments(e)}
                    role="button"
                    tabIndex={0}
                >
                    {title && <div className="message-title">{title}</div>}
                    <div className="message-content">
                        <ProtocolMessageLayout protocol={json} />
                    </div>
                    <div className="message-meta">
                        <span className="message-sender me-3">{sender === 'user' ? t('dialogs.sender.you') : t('dialogs.sender.ai')}</span>
                        <span className="message-time">{formatMessageTime(time)}</span>
                    </div>

                </div>
                <div className="message-instruments">
                    <button type="button" className="message-instrument-btn" onClick={handleCopyProtocol} title="Скопировать протокол">
                        <img src={copyIcon} alt="Copy" />
                    </button>
                    <button type="button" className="message-instrument-btn" onClick={handleOpenInstruments} title="Поделиться протоколом">
                        <img src={shareIcon} alt="Share" />
                    </button>
                    {showFeedbackUi && (<div className='d-flex gap-2'>
                        <button
                            type="button"
                            className="message-feedback-button"
                            onClick={handleLikeClick}
                            disabled={!!feedbackGiven}
                            title={t('dialogs.feedback.like')}
                            aria-label={t('dialogs.feedback.like')}
                        >
                            <img src={feedbackGiven === 'like' ? likeIcon : likeIconOutline} alt="" width={15} height={15} />
                        </button>

                        <button
                            type="button"
                            className="message-feedback-button"
                            onClick={openFeedbackModal}
                            disabled={!!feedbackGiven}
                            title={t('dialogs.feedback.dislike')}
                            aria-label={t('dialogs.feedback.dislike')}
                        >
                            <img src={feedbackGiven === 'dislike' ? dislikeIcon : dislikeIconOutline} alt="" width={15} height={15} />
                        </button>

                    </div>
                    )}
                </div>
                {isFeedbackModalOpen && showFeedbackUi && (
                    <div className="dialog-overlay" onClick={closeFeedbackModal}>
                        <div className="feedback-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="feedback-dialog-header">
                                <h3>{t('dialogs.feedback.title')}</h3>
                                <button
                                    type="button"
                                    className="dialog-close-button"
                                    onClick={closeFeedbackModal}
                                    aria-label={t('dialogs.close')}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="feedback-dialog-body">
                                <label htmlFor={`feedback-comment-${serverId ?? time}`}>
                                    {t('dialogs.feedback.commentLabel')}
                                </label>
                                <textarea
                                    id={`feedback-comment-${serverId ?? time}`}
                                    value={feedbackComment}
                                    onChange={(e) => setFeedbackComment(e.target.value)}
                                    placeholder={t('dialogs.feedback.commentPlaceholder')}
                                    maxLength={1000}
                                />
                            </div>
                            <div className="feedback-dialog-actions">
                                <button type="button" className="secondary" onClick={closeFeedbackModal} disabled={isSubmittingFeedback}>
                                    {t('dialogs.cancel')}
                                </button>
                                <button type="button" className="primary" onClick={handleSubmitFeedback} disabled={isSubmittingFeedback}>
                                    {isSubmittingFeedback ? t('dialogs.feedback.sending') : t('dialogs.feedback.submit')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Fallback для некорректных json-сообщений
    if (type === 'json') {
        return (
            <div className={`message ${alignmentClass} protocol-message`}>
                {title && <div className="message-title">{title}</div>}
                <div className="message-content">
                    <div style={{ padding: '16px', color: '#666', fontStyle: 'italic' }}>
                        {t('dialogs.message.invalidProtocol')}
                    </div>
                </div>
                <div className="message-meta">
                    <span className="message-sender me-3">{sender === 'user' ? t('dialogs.sender.you') : t('dialogs.sender.ai')}</span>
                    <span className="message-time">{formatMessageTime(time)}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`message ${alignmentClass}`}>
            {title && <div className="message-title">{title}</div>}
            <div className="message-content">
                {type === 'text' && <span className="message-text">{text}</span>}
                {type === 'photo' && <img src={text} alt="Фото сообщения" className="message-photo" />}
                {type === 'audio' && (
                    <div className="audio-message-container">
                        <p className='m-0 p-0 transcription-text-content'>{text}</p>
                        <div className="audio-message" onClick={handleAudioClick}>
                            <div className="audio-icon">
                                {isPlaying ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                )}
                            </div>
                            <div className="audio-progress">
                                <div className="audio-progress-bar">
                                    <div
                                        className="audio-progress-fill"
                                        style={{ width: `${progressPercentage}%` }}
                                    ></div>
                                </div>
                                <div className="audio-time">
                                    {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                                </div>
                            </div>
                            <div className="waveform-bar"></div>
                            <div className="waveform-bar"></div>
                            <div className="waveform-bar"></div>
                            <div className="waveform-bar"></div>
                            <div className="waveform-bar"></div>
                        </div>
                        {transcription && (
                            <button
                                className="transcribe-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleTranscription();
                                }}
                                title={showTranscription ? "Скрыть транскрипцию" : "Показать транскрипцию"}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z" />
                                    <path d="M8 12h8v2H8zm0 4h5v2H8z" />
                                </svg>
                            </button>
                        )}
                        <audio
                            ref={audioRef}
                            src={text}
                            onPlay={handleAudioPlay}
                            onPause={handleAudioPause}
                            onTimeUpdate={handleAudioTimeUpdate}
                            preload="metadata"
                        />

                    </div>
                )}
                {speakerId && (
                    <div className="message-speaker">Спикер: {speakerId}</div>
                )}
            </div>
            <div className="message-meta">
                <span className="message-sender me-3">{sender === 'user' ? t('dialogs.sender.you') : t('dialogs.sender.ai')}</span>
                <span className="message-time">{formatMessageTime(time)}</span>
            </div>

            {isFeedbackModalOpen && showFeedbackUi && (
                <div className="dialog-overlay" onClick={closeFeedbackModal}>
                    <div className="feedback-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="feedback-dialog-header">
                            <h3>{t('dialogs.feedback.title')}</h3>
                            <button
                                type="button"
                                className="dialog-close-button"
                                onClick={closeFeedbackModal}
                                aria-label={t('dialogs.close')}
                            >
                                ×
                            </button>
                        </div>
                        <div className="feedback-dialog-body">
                            <label htmlFor={`feedback-comment-${serverId ?? time}`}>
                                {t('dialogs.feedback.commentLabel')}
                            </label>
                            <textarea
                                id={`feedback-comment-${serverId ?? time}`}
                                value={feedbackComment}
                                onChange={(e) => setFeedbackComment(e.target.value)}
                                placeholder={t('dialogs.feedback.commentPlaceholder')}
                                maxLength={1000}
                            />
                        </div>
                        <div className="feedback-dialog-actions">
                            <button type="button" className="secondary" onClick={closeFeedbackModal} disabled={isSubmittingFeedback}>
                                {t('dialogs.cancel')}
                            </button>
                            <button type="button" className="primary" onClick={handleSubmitFeedback} disabled={isSubmittingFeedback}>
                                {isSubmittingFeedback ? t('dialogs.feedback.sending') : t('dialogs.feedback.submit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Message; 